import React, { useState } from "react";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import SectionHeading from "@/components/SectionHeading";
import Disclaimer from "@/components/Disclaimer";
import api, { REPORT_TIMEOUT_MS, UPLOAD_TIMEOUT_MS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/contexts/AuthContext";
import { Upload, X, ShieldCheck, FileText, Image as ImageIcon, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const isTimeoutError = (err) =>
  err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "");

/** One retry helps when Render wakes from sleep on the first request. */
async function postWithRetry(url, data, config, retries = 1) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await api.post(url, data, config);
    } catch (err) {
      lastErr = err;
      if (!isTimeoutError(err) || attempt >= retries) throw err;
      await new Promise((r) => setTimeout(r, 2500));
    }
  }
  throw lastErr;
}

const EVIDENCE_CHECKLIST = [
  { key: "screenshots", label: "Chat or screenshot proof", required: true },
  { key: "payment_proof", label: "UPI / bank payment proof", required: true },
  { key: "platform_profile", label: "Scammer profile links", required: false },
  { key: "contract", label: "Invoice or agreement", required: false },
];

const Report = () => {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [form, setForm] = useState({
    scam_type: "",
    amount_lost: "",
    platform: "",
    telegram_username: "",
    upi_id: "",
    phone: "",
    company_name: "",
    website: "",
    description: "",
    contact_email: "",
  });
  const [files, setFiles] = useState([]); // {filename, storage_path, content_type, size}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const uploadOne = async (f) => {
    if (f.size > 10 * 1024 * 1024) {
      toast.error(`${f.name}: file too large (10MB max)`);
      return null;
    }
    const fd = new FormData();
    fd.append("file", f);
    const res = await api.post("/uploads", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: UPLOAD_TIMEOUT_MS,
    });
    return res.data;
  };

  const handleFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      const results = await Promise.all(
        list.map(async (f) => {
          try {
            return await uploadOne(f);
          } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : `${f.name}: upload failed`;
            toast.error(msg);
            return null;
          }
        }),
      );
      const uploaded = results.filter(Boolean);
      if (uploaded.length) {
        setFiles((cur) => [...cur, ...uploaded]);
        toast.success(
          uploaded.length === 1 ? "1 file uploaded" : `${uploaded.length} files uploaded`,
        );
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (idx) => setFiles((cur) => cur.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    const rx = t.reportExtra || {};
    if (uploading) return toast.error(rx.waitForUpload || "Please wait for evidence uploads to finish");
    if (!form.scam_type) return toast.error(rx.selectScamType || "Select scam type");
    if (!form.description || form.description.length < 20)
      return toast.error(rx.descMin || "Description must be at least 20 characters");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        amount_lost: parseFloat(form.amount_lost || "0") || 0,
        files,
        contact_email: form.contact_email || user?.email || "",
      };
      await postWithRetry("/reports", payload, { timeout: REPORT_TIMEOUT_MS });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="mx-auto max-w-2xl px-4 md:px-8 py-32 text-center" data-testid="report-success">
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#10B981]/15 ring-1 ring-[#10B981]/40 pulse-gold">
          <CheckCircle2 className="h-8 w-8 text-[#10B981]" />
        </div>
        <h1 className={`mt-6 font-display text-3xl sm:text-4xl font-black ${lang === "hi" ? "lang-hi" : ""}`}>{t.report.successTitle}</h1>
        <p className={`mt-3 text-white/70 leading-relaxed ${lang === "hi" ? "lang-hi" : ""}`}>{t.report.successDesc}</p>
        <div className="mt-8 inline-flex gap-3">
          <Button onClick={() => { setSuccess(false); setForm({ scam_type: "", amount_lost: "", platform: "", telegram_username: "", upi_id: "", phone: "", company_name: "", website: "", description: "", contact_email: "" }); setFiles([]); }} variant="outline" className="border-white/20" data-testid="report-another-btn">
            {t.reportExtra?.submitAnother || "Submit another"}
          </Button>
          <a href="/database"><Button className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="view-db-btn">{t.nav.database}</Button></a>
        </div>
      </div>
    );
  }

  const fieldCls = "bg-white/[0.03] border-white/15 focus-visible:border-[#D4AF37] focus-visible:ring-[#D4AF37]/30 text-white placeholder:text-white/30";

  return (
    <div className="mx-auto max-w-5xl px-4 md:px-8 py-16 md:py-24" data-testid="report-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4">
          <SectionHeading eyebrow="Anonymous Intake" title={t.report.title} subtitle={t.report.subtitle} />
          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3 surface-card p-4">
              <Lock className="h-4 w-4 text-[#D4AF37] mt-0.5" />
              <div className="text-xs text-white/70 leading-relaxed">
                {t.reportExtra?.privacyIdentity}
              </div>
            </div>
            <div className="flex items-start gap-3 surface-card p-4">
              <ShieldCheck className="h-4 w-4 text-[#D4AF37] mt-0.5" />
              <div className="text-xs text-white/70 leading-relaxed">
                {t.reportExtra?.privacyReview}
              </div>
            </div>
            <Disclaimer text={t.report.disclaimer} variant="alert" />
          </div>
        </aside>

        <form onSubmit={submit} className="lg:col-span-8 surface-card p-6 md:p-8 space-y-5" data-testid="report-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.scamType} *</Label>
              <Select value={form.scam_type} onValueChange={(v) => setForm((f) => ({ ...f, scam_type: v }))}>
                <SelectTrigger className={`mt-2 ${fieldCls}`} data-testid="scam-type-select">
                  <SelectValue placeholder={t.reportExtra?.selectPlaceholder || "Select…"} />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  {t.report.types.map((tp) => (
                    <SelectItem key={tp} value={tp}>{tp}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.amountLost}</Label>
              <Input type="number" value={form.amount_lost} onChange={set("amount_lost")} className={`mt-2 ${fieldCls}`} placeholder="0" data-testid="amount-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.platform}</Label>
              <Input value={form.platform} onChange={set("platform")} className={`mt-2 ${fieldCls}`} placeholder="Telegram / WhatsApp / Web" data-testid="platform-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.telegram}</Label>
              <Input value={form.telegram_username} onChange={set("telegram_username")} className={`mt-2 ${fieldCls}`} placeholder="@example_user" data-testid="telegram-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.upi}</Label>
              <Input value={form.upi_id} onChange={set("upi_id")} className={`mt-2 ${fieldCls}`} placeholder="example@upi" data-testid="upi-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.phone}</Label>
              <Input value={form.phone} onChange={set("phone")} className={`mt-2 ${fieldCls}`} placeholder="+91 …" data-testid="phone-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.company}</Label>
              <Input value={form.company_name} onChange={set("company_name")} className={`mt-2 ${fieldCls}`} data-testid="company-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.website}</Label>
              <Input value={form.website} onChange={set("website")} className={`mt-2 ${fieldCls}`} placeholder="example.in" data-testid="website-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.description} *</Label>
            <Textarea
              value={form.description}
              onChange={set("description")}
              rows={6}
              className={`mt-2 resize-none ${fieldCls}`}
              placeholder={t.report.placeholders.description}
              data-testid="description-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.contact}</Label>
            <Input type="email" value={form.contact_email} onChange={set("contact_email")} className={`mt-2 ${fieldCls}`} placeholder="you@email.com" data-testid="contact-email-input" />
          </div>

          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-2">
            <Label className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono-alt">Evidence checklist</Label>
            <ul className="space-y-1.5 text-sm text-white/75">
              {EVIDENCE_CHECKLIST.map((item) => {
                const done = files.some((f) =>
                  (f.filename || "").toLowerCase().includes(item.key.replace("_", ""))
                  || (item.key === "screenshots" && f.content_type?.startsWith("image/"))
                  || (item.key === "payment_proof" && /payment|upi|receipt/i.test(f.filename || "")),
                );
                return (
                  <li key={item.key} className="flex items-center gap-2">
                    <CheckCircle2 className={`h-4 w-4 shrink-0 ${done ? "text-emerald-400" : "text-white/25"}`} />
                    <span>{item.label}{item.required && <span className="text-rose-400"> *</span>}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.report.uploadEvidence}</Label>
            <label htmlFor="file-upload" className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 bg-white/[0.02] px-4 py-8 hover:border-[#D4AF37]/60 hover:bg-white/[0.04] transition-colors" data-testid="file-upload-area">
              <Upload className="h-5 w-5 text-[#D4AF37]" />
              <div className="text-sm">
                <span className="text-white font-medium">{uploading ? t.common.loading : (lang === "hi" ? "क्लिक करें या फ़ाइलें खींचें" : "Click to upload files")}</span>
                <div className="text-xs text-white/50">{t.report.uploadHelp}</div>
              </div>
              <input
                id="file-upload"
                type="file"
                multiple
                accept="image/*,application/pdf,text/plain,text/csv"
                onChange={handleFiles}
                className="hidden"
                disabled={uploading}
                data-testid="file-upload-input"
              />
            </label>

            {!!files.length && (
              <div className="mt-3 space-y-2" data-testid="uploaded-files-list">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      {f.content_type?.startsWith("image/") ? <ImageIcon className="h-4 w-4 text-[#D4AF37]" /> : <FileText className="h-4 w-4 text-[#D4AF37]" />}
                      <span className="truncate text-white/85">{f.filename}</span>
                      <span className="text-white/40">{(f.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <button type="button" onClick={() => removeFile(i)} className="text-white/60 hover:text-[#FF3B30]" data-testid={`remove-file-${i}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button type="submit" disabled={submitting || uploading} className="w-full bg-[#FF3B30] hover:bg-[#cc2e25] text-white font-bold h-12 gap-2" data-testid="submit-report-btn">
            <ShieldCheck className="h-4 w-4" />
            {submitting ? t.common.loading : t.report.submit}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Report;
